import { Alert, Badge, Button, Group, Loader, SimpleGrid, Stack, Text } from '@mantine/core';
import { IconAlertTriangle, IconCoffee, IconLogin2, IconLogout2, IconPlayerPlay } from '@tabler/icons-react';
import type { PunchType } from '../../lib/api';
import { useAttendance, useLiveTotals } from '../../hooks/useAttendance';
import {
  ACTION_COLOR,
  ACTION_LABEL,
  PUNCH_LABEL,
  STATE_COLOR,
  STATE_LABEL,
  availableActions,
  formatMinutes,
  formatSince,
  isStaleShift,
  statusSentence,
} from '../../lib/attendance';

const ACTION_ICON: Record<PunchType, typeof IconLogin2> = {
  IN: IconLogin2,
  BREAK_START: IconCoffee,
  BREAK_END: IconPlayerPlay,
  OUT: IconLogout2,
};

/**
 * Stempeln (v1.405.0): Status, heutige Zeiten und genau die Knöpfe, die im Zustand gehen — groß
 * genug für den Daumen. In der Kopfzeile (Popover) und auf der Startseite.
 */
export function AttendancePunchPanel({ showPunches = false }: { showPunches?: boolean }) {
  const { status, isLoading, fetchedAt, punch, punching } = useAttendance();
  const totals = useLiveTotals(status, fetchedAt);

  if (isLoading) return <Loader size="sm" />;
  if (!status) {
    return (
      <Text size="sm" c="dimmed">
        Stempelstatus gerade nicht abrufbar.
      </Text>
    );
  }

  const actions = availableActions(status.state);
  const stale = isStaleShift(status.state, status.since);

  return (
    <Stack gap="xs">
      <Group gap="xs" wrap="nowrap">
        <Badge color={STATE_COLOR[status.state]} variant="filled" size="lg">
          {STATE_LABEL[status.state]}
        </Badge>
        <Text size="sm" fw={500}>
          {statusSentence(status.state, status.since)}
        </Text>
      </Group>
      <Text size="sm" c="dimmed">
        Heute: {formatMinutes(totals.workMinutes)} Arbeit · {formatMinutes(totals.breakMinutes)} Pause
      </Text>

      {stale && (
        <Alert color="orange" variant="light" icon={<IconAlertTriangle size={16} />} p="xs">
          <Text size="xs">
            Du bist seit {formatSince(status.since)} eingestempelt — Ausstempeln vergessen? Stempel jetzt aus und
            bitte einen Admin, die Zeit zu korrigieren.
          </Text>
        </Alert>
      )}

      <SimpleGrid cols={actions.length} spacing="xs">
        {actions.map((type) => {
          const Icon = ACTION_ICON[type];
          return (
            <Button
              key={type}
              size="md"
              color={ACTION_COLOR[type]}
              variant={type === 'OUT' ? 'light' : 'filled'}
              leftSection={<Icon size={18} />}
              loading={punching === type}
              disabled={punching !== null && punching !== type}
              onClick={() => punch(type)}
            >
              {ACTION_LABEL[type]}
            </Button>
          );
        })}
      </SimpleGrid>

      {status.jibble?.differs && (
        <Alert color="orange" variant="light" icon={<IconAlertTriangle size={16} />} p="xs">
          <Text size="xs">
            In Jibble steht ein anderer Stand: {STATE_LABEL[status.jibble.state]}
            {status.jibble.since ? ` seit ${formatSince(status.jibble.since)}` : ''}. Dort wurde offenbar
            direkt gestempelt — beim Stempeln hier wird nichts doppelt gesendet.
          </Text>
        </Alert>
      )}

      {showPunches && status.today.punches.length > 0 && (
        <Stack gap={2}>
          {status.today.punches.map((p) => (
            <Group key={p.id} gap="xs" justify="space-between" wrap="nowrap">
              <Text size="xs">
                {formatSince(p.at)} · {PUNCH_LABEL[p.type]}
                {p.source === 'ON_CALL' ? ' (Rufbereitschaft)' : p.source === 'ADMIN' ? ' (nachgetragen)' : p.source === 'JIBBLE' ? ' (aus Jibble)' : ''}
              </Text>
              {status.jibbleEnabled && (
                <Text size="xs" c={p.jibbleStatus === 'SENT' ? 'dimmed' : 'orange'}>
                  {p.jibbleStatus === 'SENT' ? 'in Jibble' : p.jibbleStatus === 'PENDING' ? 'wartet auf Jibble' : p.jibbleStatus === 'SKIPPED' ? '' : 'Jibble: Admin prüft'}
                </Text>
              )}
            </Group>
          ))}
        </Stack>
      )}
      <Text size="xs" c="dimmed">
        Zeit = Serverzeit.
        {status.jibbleEnabled ? ' Jeder Stempel geht danach an Jibble, wenn er dort nicht schon steht.' : ''}
        {status.jibble ? ` Jibble: ${STATE_LABEL[status.jibble.state]}.` : ''}
      </Text>
    </Stack>
  );
}
